import React, { FC, memo, useContext } from 'react';
import { Document, Page, Text, View } from '@react-pdf/renderer';

import styles, { BASE_FONT_SIZE } from '../pdfStyles';
import PdfLogoComponent from '../PdfComponents/PdfLogo';
import PdfFooter from '../PdfComponents/PdfFooter';
import PdfHeaderDetails from '../PdfComponents/PdfHeaderDetails';
import PdfMainDetails from '../PdfComponents/PdfMainDetails';
import PdfSection, { PdfRemarksSection } from '../PdfComponents/PdfSection';
import PdfPassengerDetails from '../PdfComponents/PdfPassengerDetails';
import PdfTableComponent from '../PdfComponents/PdfTable';
import { Context as ClientsContext } from '../../../api/clients/context';
import { Client } from '../../../api/clients/types';

import { VouchersPdfPropsType } from './voucherHelpers';

const RemittancePdf: FC<VouchersPdfPropsType> = (props: VouchersPdfPropsType) => {
    const clientsContext = useContext(ClientsContext);

    const {
        info: {
            dueDate,
            travelFileNumber,
            travelFileOpenDate,
            travelFileStartDate,
            remark,
            pdfInfo: {
                companyInfo,
                userName,
                showIssuedBy,
                voucherCertification = 'Draft',
                pdfImageSource = (clientsContext.selected as Client)?.companyLogoUrl,
                amountLineText,
                pdfBodyBoxData,
                pdfBodyBoxData2,
                topLeftDetailsText,
                bottomDetails,
                linkedTo,
                transferredFrom,
                transferredTo,
                passenger,
                columns,
                rows,
                summaryLine,
                documentsColumns,
                documentsRows,
                documentsSummaryLine
            }
        },
        number: voucherNumber,
        type: voucherType,
        createdAt
    } = props;

    return (
        <Document title={`${voucherType} ${voucherNumber || ''}`}>
            <Page wrap size="A4" style={styles.page}>
                <PdfLogoComponent pdfImageSource={pdfImageSource} />

                <View style={styles.padding0_10}>
                    <View style={styles.section}>
                        <View style={{ ...styles.header, ...styles.spaceBetween }}>
                            <View>
                                {passenger ? (
                                    <PdfPassengerDetails passenger={passenger} />
                                ) : topLeftDetailsText ? (
                                    <Text style={[styles.fontBoldAndBig1]}>{topLeftDetailsText}</Text>
                                ) : <></>}
                            </View>
                            <PdfHeaderDetails
                                companyVatNumber={companyInfo.vat}
                                iata={companyInfo.iata}
                                issueDate={props.issueDate || createdAt}
                                dueDate={dueDate}
                                travelFileNumber={travelFileNumber}
                                travelFileOpenDate={travelFileOpenDate}
                                travelFileStartDate={travelFileStartDate}
                            />
                        </View>
                    </View>
                    <View style={[styles.alignItemsFlexEnd, styles.margin2_10]}>
                        <Text style={{ ...styles.button }}>{voucherCertification}</Text>
                    </View>

                    <View style={{ ...styles.section, ...styles.alignCenter }} wrap={false}>
                        <PdfMainDetails
                            topDetails={`Payment Order No. ${voucherNumber || 'No Document Number'}`}
                            bottomDetails={bottomDetails}
                            withForLabel={bottomDetails?.length ? true : false}
                        />
                    </View>

                    {/* Amount line */}
                    {amountLineText ? (
                        <View style={[styles.section, { marginBottom: 20 }]}>
                            <Text style={{ fontSize: BASE_FONT_SIZE + 1 }}>{amountLineText}</Text>
                        </View>
                    ) : <></>}

                    {/* Amounts table: Amount | Tax | Local Price | Rate | Primary Amount.
                        Previously gated on !amountLineText, which is always set by the
                        central builder — so this table never rendered on a reopened RMT. */}
                    {columns && rows ? (
                        <View style={[styles.section, styles.marginBottom10px]}>
                            <PdfTableComponent
                                columns={columns}
                                rows={rows}
                                summaryLine={summaryLine}
                                proportionalColumns
                            />
                        </View>
                    ) : <></>}

                    <PdfSection title="Transferred from:" name={transferredFrom || ''} details={pdfBodyBoxData} />
                    <PdfSection title="Transferred to:" name={transferredTo || ''} details={pdfBodyBoxData2} />
                    <PdfRemarksSection body={linkedTo} title="Linked to" />
                    <PdfRemarksSection body={remark} />

                    {showIssuedBy !== false && !!userName && <Text style={{ fontSize: BASE_FONT_SIZE - 1, color: '#6b7280', marginTop: 6, marginHorizontal: 10 }}>Issued by: {userName}</Text>}
                </View>

                <PdfFooter companyInfo={companyInfo} />
            </Page>

            {/* Covered documents get their OWN page, not a `break` on a nested
                View: `break` is unreliable below <Page> in @react-pdf/renderer 3
                and was silently ignored here, dropping an 11-column table onto
                the bottom of page 1. A second <Page> also gets the logo and
                footer, which a break would not have. */}
            {!!documentsColumns?.length && !!documentsRows?.length && (
                <Page wrap size="A4" style={styles.page}>
                    <PdfLogoComponent pdfImageSource={pdfImageSource} />

                    <View style={styles.padding0_10}>
                        <View style={[styles.section, styles.marginBottom10px]}>
                            <Text style={[styles.fontBoldAndBig1, { marginBottom: 6 }]}>
                                {`Payment Order No. ${voucherNumber || ''} — Covered Documents`}
                            </Text>
                            <PdfTableComponent
                                columns={documentsColumns}
                                rows={documentsRows}
                                summaryLine={documentsSummaryLine}
                                proportionalColumns
                            />
                        </View>
                    </View>

                    <PdfFooter companyInfo={companyInfo} />
                </Page>
            )}
        </Document>
    );
};

export default memo(RemittancePdf);
